import { MoveRight } from "lucide-react";
import { useState, useEffect, useId } from "react";
import { motion } from "framer-motion"

const toPolylinePoints = (pts) => pts.map(([x, y]) => `${x},${y}`).join(" ");
const toAreaPath = (pts, baseline) => {
    const line = pts.map(([x, y]) => `${x},${y}`).join(" L ");
    const [firstX] = pts[0];
    const [lastX] = pts[pts.length - 1];
    return `M ${firstX},${baseline} L ${line} L ${lastX},${baseline} Z`;
};

export const Word = ({ word }) => {
    return (
        <div className="w-max bg-white/60 backdrop-blur rounded-2xl p-3 px-5 flex flex-col gap-3 shadow-sm">
            <div className="flex gap-3 items-center">
                <p className="text-base-text text-[16px]">{word}</p>

                <div className="flex gap-1 items-center">
                    <span className="rounded-[5px] bg-[#DCA6A6] w-6 h-3" />
                    <span className="rounded-[5px] bg-[#A6BBDC] w-5 h-3" />
                    <span className="rounded-[5px] bg-[#8CCCD9] w-6 h-3" />
                </div>
            </div>

            <div className="font-light text-base-text text-sm pl-2">
                <p>示す</p>
                <p>知らせる</p>
                <p>合図する</p>
            </div>
        </div>
    )
}
export const Correction = () => {
    return (
        <div className="backdrop-blur bg-white/50 rounded-2xl p-3 flex flex-col gap-4 absolute mt-1 top-full -left-4 min-w-[210px]">
            <div className="flex justify-between items-center">
                <div className="flex gap-2 items-center text-base-text text-xs">
                    <p className="line-through">I become to</p>
                    <MoveRight className="text-base-text size-5" strokeWidth={1} />
                    <p>I started to</p>
                </div>
            </div>

            <div className="flex gap-1 items-start">
                <BotIcon size={22} className="shrink-0" />
                <p className="text-xs text-base-text">「好きになる」を become で組み立てたんですね。英語では、その変化を動詞ひとつで言うことが多いです。</p>

            </div>
        </div>
    )
}
export function GrowthChartCard({ className }) {
    const BASELINE = 660;
    const VIEW_W = 900;

    const BLUE_POINTS = [
        [60, 640],
        [150, 600],
        [230, 592],
        [270, 555],
        [350, 555],
        [430, 528],
        [510, 528],
        [570, 460],
        [630, 428],
        [690, 372],
        [750, 360],
        [860, 300],
    ];

    const GREEN_POINTS = [
        [60, 610],
        [150, 565],
        [230, 552],
        [270, 510],
        [320, 470],
        [370, 440],
        [430, 425],
        [480, 395],
        [540, 365],
        [610, 305],
        [690, 235],
        [790, 180],
        [860, 115],
    ];

    const [drawn, setDrawn] = useState(false);
    const uid = useId();
    const blueClipId = `blue-clip-${uid}`;
    const greenClipId = `green-clip-${uid}`;

    useEffect(() => {
        const id = requestAnimationFrame(() => setDrawn(true));
        return () => cancelAnimationFrame(id);
    }, []);

    const bluePoints = toPolylinePoints(BLUE_POINTS);
    const greenPoints = toPolylinePoints(GREEN_POINTS);
    const blueArea = toAreaPath(BLUE_POINTS, BASELINE);

    const blueEnd = BLUE_POINTS[BLUE_POINTS.length - 1];
    const greenEnd = GREEN_POINTS[GREEN_POINTS.length - 1];
    const dotR = 11;

    return (
        <div className={`flex items-center justify-center`}>
            <div className={`relative w-full overflow-hidden  ${className}`}>
                <svg
                    viewBox="0 0 900 700"
                    className="w-full h-auto"
                    role="img"
                    aria-label="Line chart showing words encountered rising to 1437 and words learned rising to 437"
                >
                    <defs>
                        <clipPath id={blueClipId}>
                            <rect
                                x="0"
                                y="0"
                                height="700"
                                width={drawn ? VIEW_W : 0}
                                style={{ transition: "width 1.4s ease-out" }}
                            />
                        </clipPath>
                        <clipPath id={greenClipId}>
                            <rect
                                x="0"
                                y="0"
                                height="700"
                                width={drawn ? VIEW_W : 0}
                                style={{ transition: "width 1.4s ease-out 0.25s" }}
                            />
                        </clipPath>
                    </defs>

                    {/* baseline axis */}
                    {/* <line x1="60" y1={BASELINE} x2="860" y2={BASELINE} stroke="#9db6c6" strokeWidth="2" /> */}

                    <g clipPath={`url(#${blueClipId})`}>
                        {/* filled area under the blue line */}
                        <path d={blueArea} fill="#87b7d3ff" fillOpacity="0.22" />
                        {/* blue solid line */}
                        <polyline
                            points={bluePoints}
                            fill="none"
                            stroke="#87b7d3ff"
                            strokeWidth="7"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </g>

                    <g clipPath={`url(#${greenClipId})`}>
                        {/* green dashed line */}
                        <polyline
                            points={greenPoints}
                            fill="none"
                            stroke="#6db69cff"
                            strokeWidth="7"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeDasharray="16 14"
                        />
                    </g>

                    {/* end dots */}
                    <circle
                        cx={blueEnd[0]}
                        cy={blueEnd[1]}
                        r={dotR}
                        fill="#87b7d3ff"
                        style={{
                            opacity: drawn ? 1 : 0,
                            transition: "opacity 0.5s ease-out 1.3s",
                        }}
                    />
                    <circle
                        cx={greenEnd[0]}
                        cy={greenEnd[1]}
                        r={dotR}
                        fill="#6db69cff"
                        style={{
                            opacity: drawn ? 1 : 0,
                            transition: "opacity 0.5s ease-out 1.55s",
                        }}
                    />

                    {/* labels, anchored to the left of each dot */}
                    <text
                        x={greenEnd[0] - dotR - 14}
                        y={greenEnd[1]}
                        textAnchor="end"
                        dominantBaseline="middle"
                        style={{
                            opacity: drawn ? 1 : 0,
                            transition: "opacity 0.6s ease-out 1.55s",
                        }}
                    >
                        <tspan fill="#27485C" fontSize="34" fontFamily="'M PLUS Rounded 1c', sans-serif" fontWeight={300}>
                            出会った言葉
                        </tspan>
                        <tspan fill="#459d7eff" fontSize="34" fontFamily="'M PLUS Rounded 1c', sans-serif" dx="14">
                            ｜
                        </tspan>
                        <tspan fill="#459d7eff" fontSize="34" fontFamily="'M PLUS Rounded 1c', sans-serif" dx="4">
                            1602
                        </tspan>
                    </text>

                    <text
                        x={blueEnd[0] - dotR - 14}
                        y={blueEnd[1]}
                        textAnchor="end"
                        dominantBaseline="middle"
                        style={{
                            opacity: drawn ? 1 : 0,
                            transition: "opacity 0.6s ease-out 1.3s",
                        }}
                    >
                        <tspan fill="#27485C" fontSize="34" fontFamily="'M PLUS Rounded 1c', sans-serif" fontWeight={300}>
                            身についた言葉
                        </tspan>
                        <tspan fill="#4b95c0ff" fontSize="34" fontFamily="'M PLUS Rounded 1c', sans-serif" dx="14">
                            ｜
                        </tspan>
                        <tspan fill="#4b95c0ff" fontSize="34" fontFamily="'M PLUS Rounded 1c', sans-serif" dx="4">
                            437
                        </tspan>
                    </text>
                </svg>
            </div>
        </div>
    );
}



const containerVariants = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.06,
            delayChildren: 0.1,
        },
    },
};

const nodeVariants = {
    hidden: { opacity: 0, scale: 0.3 },
    show: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.25, ease: "easeOut" },
    },
};

const dotVariants = {
    hidden: { opacity: 0, scale: 0 },
    show: (delay = 0) => ({
        opacity: 1,
        scale: 1,
        transition: {
            duration: 0.3,
            ease: "easeOut",
            delay: delay, // Reads dynamic delay
        },
    }),
};

const edgeVariants = {
    hidden: {
        pathLength: 0,
        opacity: 0
    },
    show: (delay = 0) => ({
        pathLength: 1,
        opacity: 1,
        transition: {
            pathLength: { duration: 0.4, ease: "easeInOut", delay },
            opacity: { duration: 0.01, delay }, // Instant fade-in so the line head is visible immediately
        },
    }),
};
export function QuestionNetworkCard({
    count = 67,
    label = "生まれた問い",
}) {
    const CARD_W = 320;
    const CARD_H = 240; // visible rounded card area
    const TOTAL_H = 300; // extra room below the card for the tail + dot

    const NODES = [
        { x: 270, y: 55, r: 7 }, // top right
        { x: 75, y: 105, r: 7 }, // left
        { x: 170, y: 120, r: 7 }, // center
        { x: 100, y: 150, r: 5 }, // mid-left (tail starts here)
        { x: 245, y: 150, r: 5 }, // right
        { x: 195, y: 205, r: 5 }, // bottom center
    ];

    const EDGES = [
        [0, 2],
        [2, 3], //
        [1, 3],
        [2, 4],
        [4, 5],
        [2, 5],
        [2, 5],
    ];

    const ACCENT_DOTS = [
        { x: 280, y: 110 },
        { x: 130, y: 197 },
    ];

    const TAIL_END = { x: 40, y: 270 };

    // Timing for the sequence after the main graph settles.
    // Main stagger: delayChildren(0.1) + staggerChildren(0.06) * 15 children ≈ finishes ~1.1s + node duration(0.25) ≈ 1.35s
    const dotDelay = 0.3;
    const tailEdgeDelay = dotDelay + 0.3; // 1.7
    const popupDelay = tailEdgeDelay + 0.1; // 2.05

    return (
        <div
            className="relative w-[280px] md:w-[320px] shrink-0"
            style={{ aspectRatio: `${CARD_W} / ${TOTAL_H}` }}
        >
            <svg
                viewBox={`0 0 ${CARD_W} ${TOTAL_H}`}
                preserveAspectRatio="none"
                className="w-full h-full"
                role="img"
                aria-label={`${label}: ${count}`}
            >
                {/* card background */}
                <rect x="0" y="0" width={CARD_W} height={CARD_H} rx="20" fill="rgba(255,255,255,0.5)" className="drop-shadow-[0_1px_2px_rgba(0,0,0,0.06)]" />

                {/* title */}
                <text
                    x="20"
                    y="36"
                    fontSize="14"
                    fontFamily="'M PLUS Rounded 1c', sans-serif"
                    fontWeight={300}
                    fill="#3d4a5c"
                >
                    <tspan>{label}</tspan>
                    <tspan fill="#8a85a4ff"> {" | "} </tspan>
                    <tspan>{count}</tspan>
                </text>

                {/* ...then edges connect to it */}
                <motion.line
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.4 }}
                    variants={edgeVariants}
                    custom={tailEdgeDelay}
                    x1={NODES[3].x}
                    y1={NODES[3].y}
                    x2={TAIL_END.x}
                    y2={TAIL_END.y}
                    stroke="#ACA6C7"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                // style={{ mixBlendMode: "difference" }}
                />
                <motion.line
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.4 }}
                    variants={edgeVariants}
                    custom={tailEdgeDelay}
                    x1={NODES[5].x}
                    y1={NODES[5].y}
                    x2={TAIL_END.x}
                    y2={TAIL_END.y}
                    stroke="#ACA6C7"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />

                {/* main network: edges, accent dots, nodes — fade/scale in together with stagger */}
                <motion.g
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.4 }}
                    variants={containerVariants}
                >
                    {/* edges */}
                    {EDGES.map(([a, b], i) => (
                        <motion.line
                            key={i}
                            variants={edgeVariants}
                            x1={NODES[a].x}
                            y1={NODES[a].y}
                            x2={NODES[b].x}
                            y2={NODES[b].y}
                            stroke="#ACA6C7"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                        />
                    ))}

                    {/* small accent dots */}
                    {ACCENT_DOTS.map((d, i) => (
                        <motion.circle key={i} variants={nodeVariants} cx={d.x} cy={d.y} r="4" fill="#C59385" />
                    ))}

                    {/* nodes */}
                    {NODES.map((n, i) => (
                        <motion.circle key={i} variants={nodeVariants} cx={n.x} cy={n.y} r={n.r} fill="#ACA6C7" />
                    ))}
                </motion.g>

                {/* green dot appears first... */}
                <motion.circle
                    initial="hidden"
                    animate="show"
                    // viewport={{ once: true, amount: 0.01 }}
                    variants={dotVariants}
                    custom={dotDelay}
                    cx={TAIL_END.x}
                    cy={TAIL_END.y}
                    r="6"
                    fill="#AEDCCD"
                />


            </svg>

            {/* popup appears last */}
            <motion.div
                initial={{ opacity: 0, y: -5 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.3, ease: "easeOut", delay: popupDelay }}
                className="absolute top-full -mt-8 ml-10 md:ml-12 bg-white/50 shadow-sm backdrop-blur-sm rounded-2xl p-3"
            >
                <p className="text-base-text font-light text-xs">
                    アオメ、signalのあと、いつもthatが来るのかな？ それとも、ここだけ？
                </p>
            </motion.div>
        </div>
    );
}

export const BotIcon = ({
    size = 28,
    className = "",
    intervalSeconds = 5,
    strokeWidth = 1,
    fill = false,
    outlineColor = "#3A4550"
}) => {
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setIsAnimating(true);
        }, intervalSeconds * 1000);

        return () => clearInterval(interval);
    }, [intervalSeconds]);

    useEffect(() => {
        if (isAnimating) {
            const t = setTimeout(() => setIsAnimating(false), 1300);
            return () => clearTimeout(t);
        }
    }, [isAnimating]);

    const eyeStyle = {
        transformOrigin: 'center',
        animation: isAnimating ? 'botEyeMove 1.3s ease-in-out' : 'none',
    };

    return (
        <>
            <style>{`
                @keyframes botEyeMove {
                    0% { transform: translateX(0) translateY(0); }
                    33% { transform: translateX(-1.5px) translateY(1.5px); }
                    66% { transform: translateX(1.5px) translateY(1.5px); }
                    100% { transform: translateX(0) translateY(0); }
                }
            `}</style>

            <svg
                xmlns="http://www.w3.org/2000/svg"
                width={size}
                height={size}
                viewBox="0 0 24 24"
                fill="none"
                stroke={outlineColor}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                className={className}
            >
                <g transform="translate(1, -5) scale(0.9)" fill={"none"} stroke={outlineColor}>
                    <path d="M12 9.5v-1.5a2 2 0 0 1 2-2h1a.5.5 0 0 1 .5.5V7a2 2 0 0 1-2 2 2 2 0 0 0-2 2" />
                    <path d="M8 8.5a3 3 0 0 1 4 3 3 3 0 0 1-4-3" />
                </g>

                <path
                    d="M12 7.5C16.1 7.5 18.8 8.6 20.6 10.5C22.4 12.5 23.2 15.3 23.4 18.6C24.5 21.2 23.7 23.9 21.6 26C19.5 28.1 16.1 29.5 12 29.5C7.9 29.5 4.4 28.2 2.2 26.2C0.1 24.2 -0.7 21.5 0.6 18.5C1.4 15.2 2.3 12.5 4.1 10.5C5.9 8.6 8.8 7.5 12 7.5Z"
                    transform="scale(0.7) translate(5, 1)"
                    fill={"none"}
                />

                <path d="M15 13v2" style={eyeStyle} />
                <path d="M9 13v2" style={eyeStyle} />
            </svg>
        </>
    );
};
export const FooterLogo = () => {
    return (<svg width="34" height="18" viewBox="0 0 34 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white">
        <path d="M9.45675 17.9226C8.30698 18.0988 7.20809 17.2958 7.29225 16.1286C7.33917 15.4777 7.45289 14.8316 7.63273 14.2007C8.00688 12.8882 8.6584 11.6725 9.54271 10.6368C10.427 9.60106 11.5232 8.76981 12.7564 8.19989C13.9895 7.62998 15.3305 7.33485 16.6874 7.3347C18.0444 7.33455 19.3854 7.62939 20.6187 8.19904C21.8519 8.76868 22.9483 9.5997 23.8329 10.6352C24.7174 11.6707 25.3692 12.8863 25.7436 14.1988C25.9236 14.8296 26.0375 15.4757 26.0845 16.1266C26.1689 17.2938 25.0702 18.0969 23.9204 17.921C22.7706 17.7451 22.0229 16.6336 21.7314 15.5006C21.72 15.4563 21.708 15.4122 21.6955 15.3682C21.4884 14.6424 21.128 13.9703 20.6389 13.3977C20.1498 12.8251 19.5436 12.3656 18.8617 12.0506C18.1797 11.7357 17.4382 11.5726 16.6879 11.5727C15.9376 11.5728 15.1961 11.736 14.5142 12.0511C13.8324 12.3662 13.2262 12.8259 12.7373 13.3986C12.2483 13.9713 11.888 14.6435 11.6811 15.3693C11.6686 15.4133 11.6566 15.4574 11.6452 15.5017C11.354 16.6347 10.6065 17.7464 9.45675 17.9226Z" fill="currentColor" />
        <path d="M0 16.1103C0 15.0931 0.819475 14.2685 1.83035 14.2685H3.66069C4.67157 14.2685 5.49104 15.0931 5.49104 16.1103C5.49104 17.1275 4.67157 17.9521 3.66069 17.9521H1.83035C0.819473 17.9521 0 17.1275 0 16.1103Z" fill="currentColor" />
        <path d="M27.8859 16.1102C27.8859 15.0931 28.7053 14.2685 29.7162 14.2685H31.5466C32.5574 14.2685 33.3769 15.0931 33.3769 16.1102C33.3769 17.1274 32.5574 17.952 31.5466 17.952H29.7162C28.7053 17.952 27.8859 17.1274 27.8859 16.1102Z" fill="currentColor" />
        <path d="M16.9038 5.52539C15.8929 5.52539 15.0734 4.70079 15.0734 3.68359V1.8418C15.0734 0.824599 15.8929 0 16.9038 0C17.9147 0 18.7341 0.824601 18.7341 1.8418V3.68359C18.7341 4.70079 17.9147 5.52539 16.9038 5.52539Z" fill="currentColor" />
        <path d="M23.0647 6.8653C22.1893 6.3567 21.8893 5.23027 22.3948 4.34936L23.31 2.75431C23.8154 1.8734 24.9348 1.57157 25.8103 2.08017C26.6857 2.58877 26.9856 3.71519 26.4802 4.59611L25.565 6.19115C25.0596 7.07207 23.9402 7.37389 23.0647 6.8653Z" fill="currentColor" />
        <path d="M26.7554 10.934C26.2499 10.0531 26.5499 8.92667 27.4253 8.41807L29.0104 7.49717C29.8859 6.98857 31.0053 7.2904 31.5107 8.17132C32.0162 9.05223 31.7162 10.1787 30.8408 10.6873L29.2557 11.6082C28.3802 12.1168 27.2608 11.8149 26.7554 10.934Z" fill="currentColor" />
        <path d="M2.34715 8.17132C2.85259 7.2904 3.97201 6.98857 4.84745 7.49717L6.43258 8.41807C7.30802 8.92667 7.60797 10.0531 7.10253 10.934C6.59709 11.8149 5.47767 12.1167 4.60223 11.6082L3.0171 10.6873C2.14166 10.1787 1.84171 9.05223 2.34715 8.17132Z" fill="currentColor" />
        <path d="M7.99139 2.08015C8.86683 1.57155 9.98625 1.87338 10.4917 2.75429L11.4069 4.34934C11.9123 5.23025 11.6124 6.35668 10.7369 6.86528C9.86147 7.37387 8.74204 7.07205 8.23661 6.19113L7.32143 4.59609C6.816 3.71517 7.11595 2.58875 7.99139 2.08015Z" fill="currentColor" />
    </svg>)

}
